// src/mobile_pages/const/Order.jsx
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  FileText,
  Calendar,
  User,
  CheckCircle2,
  Clock
} from "lucide-react";
import { getWorkOrderList } from "../../api/const";

export default function WorkOrderBoard() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);

  // 검색 필터
  const [q, setQ] = useState("");
  const [searchField, setSearchField] = useState("title");

  const fetchData = async () => {
    if (!projectId) return;
    setLoading(true);

    try {
      const params = {
        project: projectId,
        [searchField]: q,
        page,
        page_size: pageSize,
      };

      const res = await getWorkOrderList(projectId, params);
      setRows(res.results ?? res);
      setTotal(res.count ?? res.total ?? res.length);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [projectId, page, pageSize]);

  const handleSearch = () => {
    setPage(1);
    fetchData();
  };

  // 상태 배지
  const getStatusInfo = (row) => {
    const hasCompletion = row.completion?.length > 0;
    if (hasCompletion) {
      return {
        label: "완료",
        bgColor: "bg-green-50",
        textColor: "text-green-600",
        borderColor: "border-green-200",
        icon: <CheckCircle2 size={12} />,
      };
    }
    return {
      label: "진행중",
      bgColor: "bg-amber-50",
      textColor: "text-amber-600",
      borderColor: "border-amber-200",
      icon: <Clock size={12} />,
    };
  };

  // 페이지네이션 계산
  const totalPages = Math.ceil(total / pageSize) || 1;
  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden w-full">
      <main className="w-full px-4 pt-6 pb-8">
        {/* 헤더 */}
        <header className="mb-4">
          <div className="flex items-center justify-between gap-3 min-w-0">
            <div>
              <h1 className="text-xl font-bold text-slate-900">작업지시서</h1>
              <p className="text-[13px] text-slate-500">
                총 {total}건의 작업지시서가 등록되어 있습니다
              </p>
            </div>
            <button
              onClick={() => navigate(`new`)}
              className="flex-shrink-0 flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-[13px] font-semibold text-white shadow-sm active:scale-[0.97] transition-transform"
            >
              <Plus size={16} strokeWidth={2.5} />
              새 문서
            </button>
          </div>
        </header>

        {/* 검색 영역 */}
        <section className="mb-4">
          <div className="flex items-center gap-1.5 rounded-xl border bg-white px-2 py-2 shadow-sm min-w-0">
            <select
              value={searchField}
              onChange={(e) => setSearchField(e.target.value)}
              className="flex-shrink-0 bg-transparent text-[11px] text-slate-600 font-medium outline-none border-r border-slate-200 pr-1"
            >
              <option value="title">제목</option>
              <option value="author">작성자</option>
            </select>
            <Search size={14} className="text-slate-400 flex-shrink-0" />
            <input
              className="flex-1 min-w-0 bg-transparent text-[13px] text-slate-800 placeholder-slate-400 outline-none"
              placeholder="검색어 입력"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
            <button
              onClick={handleSearch}
              className="flex-shrink-0 rounded-lg bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-600 active:scale-95"
            >
              검색
            </button>
          </div>
        </section>

        {/* 목록 */}
        <section className="space-y-3">
          {loading ? (
            <p className="py-12 text-center text-[15px] text-slate-400">
              불러오는 중...
            </p>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center">
              <FileText size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-[15px] text-slate-400">등록된 작업지시서가 없습니다</p>
              <p className="text-xs text-slate-400 mt-1">새 작업지시서를 등록해보세요</p>
            </div>
          ) : (
            rows.map((row) => {
              const status = getStatusInfo(row);
              return (
                <article
                  key={row.id}
                  onClick={() => navigate(`${row.dns}/view`)}
                  className="relative rounded-2xl border border-slate-200 bg-white p-4 shadow-sm active:scale-[0.99] transition-transform cursor-pointer"
                >
                  {/* 상단: 제목 + 상태 */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                        <FileText size={20} className="text-indigo-500" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-[15px] font-semibold text-slate-800 line-clamp-2">
                          {row.title || "(제목 없음)"}
                        </h3>
                      </div>
                    </div>
                    {/* 상태 배지 */}
                    <span className={`flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium border ${status.bgColor} ${status.textColor} ${status.borderColor}`}>
                      {status.icon}
                      {status.label}
                    </span>
                  </div>

                  {/* 메타 정보 */}
                  <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 pl-13">
                    <span className="flex items-center gap-1">
                      <User size={12} />
                      {row.created_by?.name || "-"}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar size={12} />
                      {row.created_at
                        ? new Date(row.created_at).toLocaleDateString("ko-KR")
                        : "-"}
                    </span>
                    {row.approval?.approver_name && (
                      <span className="flex items-center gap-1 text-blue-500">
                        <CheckCircle2 size={12} />
                        {row.approval.approver_name}
                      </span>
                    )}
                  </div>

                  {/* 선택 화살표 */}
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                    <ChevronRight size={20} className="text-slate-300" />
                  </div>
                </article>
              );
            })
          )}
        </section>

        {/* 페이지네이션 */}
        {rows.length > 0 && (
          <nav className="mt-6 flex items-center justify-center gap-2">
            <button
              disabled={!canPrev}
              onClick={() => setPage((p) => p - 1)}
              className={`flex h-9 w-9 items-center justify-center rounded-full border ${canPrev
                ? "border-slate-300 text-slate-600 active:scale-95"
                : "border-slate-100 text-slate-300"
                }`}
            >
              <ChevronLeft size={18} />
            </button>

            <span className="text-sm text-slate-600 px-3">
              {page} / {totalPages}
            </span>

            <button
              disabled={!canNext}
              onClick={() => setPage((p) => p + 1)}
              className={`flex h-9 w-9 items-center justify-center rounded-full border ${canNext
                ? "border-slate-300 text-slate-600 active:scale-95"
                : "border-slate-100 text-slate-300"
                }`}
            >
              <ChevronRight size={18} />
            </button>
          </nav>
        )}
      </main>
    </div>
  );
}
